import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Paper, TableBody, TableCell, TableRow } from "@mui/material";
import styled from "@emotion/styled";

import Pagination from "Components/Common/BaseTable/Pagination";
import { usePagination } from "Components/Common/BaseTable";
import { StyledTable, StyledTableCell, StyledTableHead } from "./style";
import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";

const RECORDS_PER_PAGE = 10;
const emptyValues = [null, undefined, ""];

const StyledTableContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "maxHeight",
})`
  max-height: ${({ maxHeight }) => maxHeight ?? "unset"};
  overflow-y: auto;
  .MuiPaper-root {
    overflow-x: scroll;
  }
`;

const MonthlyApplicationCountTable = ({ data, maxHeight, t }) => {
  const paginate = usePagination();
  const [rowData, setRowData] = useState([]);
  const { page, setTotalPages } = paginate;

  const headerName = useMemo(
    () =>
      data?.[0]?.monthlyApplicationCountReport?.[0]
        ? Object.keys(data[0].monthlyApplicationCountReport[0])
        : [],
    [data]
  );

  const typeOfApplicationCounts = useMemo(
    () =>
      rowData.reduce(
        (acc, { applicationTypeName, monthlyApplicationCountReport }) => {
          if (!applicationTypeName) {
            return acc;
          }

          const obj = monthlyApplicationCountReport?.[0] || {};

          const counts = Object.values(obj).reduce(
            (counts, item, index) => ({
              ...counts,
              [`count${index}`]: emptyValues.includes(item?.count)
                ? "--"
                : item.count,
            }),
            {}
          );

          return [
            ...acc,
            {
              applicationTypeName,
              ...counts,
            },
          ];
        },
        []
      ),
    [rowData]
  );

  useEffect(() => {
    if (data?.length) {
      setTotalPages(Math.ceil(data.length / RECORDS_PER_PAGE));
    }
  }, [data]);

  useEffect(() => {
    const rowData =
      data?.slice(RECORDS_PER_PAGE * (page - 1), RECORDS_PER_PAGE * page) || [];
    setRowData(rowData);
  }, [page, data]);

  return (
    <>
      <StyledTableContainer maxHeight={maxHeight}>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell rowSpan={2}>
                {t("Type Of Application")}
              </StyledTableCell>
              {headerName?.map((header, index) => (
                <React.Fragment key={index}>
                  <StyledTableCell align="center">
                    {t(convertCamelTextToCapitalizeText(header))}
                  </StyledTableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {typeOfApplicationCounts.length ? (
              typeOfApplicationCounts.map(
                ({ applicationTypeName, ...rest }, index) => (
                  <TableRow key={index}>
                    <TableCell>{applicationTypeName}</TableCell>
                    {Object.values(rest)?.map((count, index) => (
                      <TableCell key={index} align="center">
                        {count}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              )
            ) : (
              <TableRow>
                <TableCell colSpan={10} className="text-center">
                  {t("No Data Available")}!
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      {!!typeOfApplicationCounts?.length && <Pagination paginate={paginate} />}
    </>
  );
};

MonthlyApplicationCountTable.propTypes = {
  data: PropTypes.array,
  maxHeight: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(MonthlyApplicationCountTable);
